import dayjs from "utils/dayjs";
import { Box, ButtonBase, Typography } from "@mui/material";
import RenderMerchantDisplay from "components/common/RenderMerchantDisplay";
import { formatCashFull } from "utils/formatCash";
import { isReceiptTransactionDateMissing } from "utils/receipts/receiptFormat";
import { transactionMerchantName } from "utils/transactions/transactionRow";
import { RenderCompliance } from "components/common/RenderCompliance";
import type { TransactionNode } from "./ReceiptConnectDrawer";

interface TransactionResultRowProps {
  transaction: TransactionNode;
  onClick?: () => void;
  disabled?: boolean;
}

const TransactionResultRow: React.FC<TransactionResultRowProps> = ({
  transaction,
  onClick,
  disabled,
}) => {
  return (
    <Box sx={{ paddingY: "var(--space-3)" }}>
      {/* A real button, not a clickable div: picking a transaction is the whole
          job of this drawer and it has to be reachable by keyboard. */}
      <ButtonBase
        onClick={onClick}
        disabled={disabled}
        sx={{
          display: "flex",
          width: "100%",
          justifyContent: "space-between",
          alignItems: "center",
          textAlign: "left",
          paddingY: "var(--space-2)",
          paddingX: "var(--space-4)",
          borderRadius: "var(--radius-md)",
          transition: "background-color 0.15s ease",
          "&:hover": { backgroundColor: "var(--color-bg-elevated)" },
          "&:focus-visible": {
            outline: "var(--border-width-2) solid var(--color-border-focus)",
            outlineOffset: "var(--border-width-1)",
          },
          "&.Mui-disabled": { opacity: 0.5 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: "var(--space-5)",
            alignItems: "center",
            minWidth: 0,
          }}
        >
          <RenderMerchantDisplay
            merchant={transactionMerchantName(transaction)}
            url={transaction.merchantData?.url ?? undefined}
            logo={transaction.merchantData?.logo ?? undefined}
            cachedLogo={transaction.merchantData?.cachedLogo ?? undefined}
            withTitle={false}
            withLink={false}
          />
          <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            <Typography
              variant="caption"
              component="span"
              sx={{ color: "var(--color-text-secondary)" }}
            >
              {isReceiptTransactionDateMissing(transaction.transactionDate)
                ? "-"
                : dayjs(transaction.transactionDate).format("MMM D, YYYY")}
            </Typography>
            <Typography
              variant="subtitle2"
              component="span"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {transactionMerchantName(transaction)}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Typography variant="subtitle2" component="span">
            {formatCashFull(transaction.amount, transaction.currency)}
          </Typography>
          <RenderCompliance
            policyStatus={transaction.policyStatus}
            policyStatusDisplay={transaction.policyStatusDisplay}
            sx={{ padding: "var(--space-2) 0" }}
          />
        </Box>
      </ButtonBase>
    </Box>
  );
};

export default TransactionResultRow;
